import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Send, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import { useDmSocket } from "../lib/community";
import { Avatar, KindBadge } from "../components/community/CommunityKit";

const time = (iso) => new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

function ThreadList({ threads, active }) {
  if (!threads) return <div className="p-4 space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-14 rounded-2xl bg-[var(--bg)] animate-pulse" />)}</div>;
  if (!threads.length) return (
    <div className="p-8 text-center text-sm text-[var(--muted)]" data-testid="threads-empty">
      <MessageCircle className="w-8 h-8 mx-auto text-[var(--gold)] mb-3" />No conversations yet. Visit a profile in the <Link to="/community/directory" className="text-[var(--gold)] underline">directory</Link> and tap Message.
    </div>
  );
  return (
    <ul className="divide-y divide-[var(--line)]" data-testid="thread-list">
      {threads.map((t) => t.member && (
        <li key={t.thread_id}>
          <Link to={`/messages/${t.thread_id}`} data-testid={`thread-${t.thread_id}`} className={`flex items-center gap-3 px-4 py-3 transition-colors ${active === t.thread_id ? "bg-[var(--bg-warm)]" : "hover:bg-[var(--bg)]"}`}>
            <Avatar p={t.member} size="w-11 h-11" />
            <div className="flex-1 min-w-0">
              <p className={`text-sm truncate ${t.unread_count ? "font-semibold" : ""}`}>{t.member.display_name}</p>
              <p className="text-xs text-[var(--muted)] truncate">{t.last_message || "Say hello"}</p>
            </div>
            {t.unread_count > 0 && <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-[var(--gold)] text-white text-[10px] flex items-center justify-center" data-testid={`thread-unread-${t.thread_id}`}>{t.unread_count}</span>}
          </Link>
        </li>
      ))}
    </ul>
  );
}

function Chat({ thread, me, messages, onSend }) {
  const [text, setText] = useState("");
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);
  async function send(e) {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    setText("");
    await onSend(t);
  }
  return (
    <div className="flex flex-col h-full" data-testid="chat-pane">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[var(--line)] bg-white">
        <Link to="/messages" className="md:hidden p-1 -ml-1" data-testid="chat-back"><ArrowLeft className="w-5 h-5" /></Link>
        {thread.member && <Link to={`/community/u/${thread.member.handle}`} className="flex items-center gap-3 min-w-0"><Avatar p={thread.member} size="w-9 h-9" /><div className="min-w-0"><p className="text-sm font-medium truncate">{thread.member.display_name}</p><KindBadge kind={thread.member.kind} /></div></Link>}
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2 bg-[var(--bg)]" data-testid="chat-messages">
        {messages.map((m) => {
          const mine = m.sender_id === me;
          return (
            <motion.div key={m.msg_id} initial={{ opacity: 0, y: 8, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[78%] rounded-3xl px-4 py-2.5 text-[15px] ${mine ? "bg-[var(--ink)] text-white rounded-br-md" : "bg-white border border-[var(--line)] rounded-bl-md"}`} data-testid={`chat-msg-${m.msg_id}`}>
                <p className="whitespace-pre-wrap break-words">{m.text}</p>
                <p className={`text-[10px] mt-1 ${mine ? "text-white/50" : "text-[var(--muted)]"}`}>{time(m.created_at)}</p>
              </div>
            </motion.div>
          );
        })}
        <div ref={end} />
      </div>
      <form onSubmit={send} className="flex gap-2 p-3 border-t border-[var(--line)] bg-white" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} placeholder="Message…" data-testid="chat-input" className="flex-1 min-w-0 bg-[var(--bg)] rounded-full px-4 py-3 text-[15px] outline-none focus:ring-1 focus:ring-[var(--gold)]" />
        <motion.button whileTap={{ scale: 0.9 }} disabled={!text.trim()} data-testid="chat-send" className="w-12 h-12 rounded-full bg-[var(--gold)] text-white flex items-center justify-center disabled:opacity-40"><Send className="w-5 h-5" /></motion.button>
      </form>
    </div>
  );
}

export default function MessagesPage() {
  const { threadId } = useParams();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [threads, setThreads] = useState(null);
  const [messages, setMessages] = useState([]);
  const active = threads?.find((t) => t.thread_id === threadId);

  const loadThreads = useCallback(() => api.get("/dm/threads").then((r) => setThreads(r.data)).catch(() => setThreads([])), []);
  useEffect(() => { if (!loading && !user) nav(`/login?next=/messages${threadId ? `/${threadId}` : ""}`); }, [loading, user, nav, threadId]);
  useEffect(() => { if (user) loadThreads(); }, [user, loadThreads]);
  useEffect(() => {
    if (!threadId || !user) return;
    setMessages([]);
    api.get(`/dm/threads/${threadId}/messages`).then((r) => { setMessages(r.data); setThreads((ts) => ts?.map((t) => (t.thread_id === threadId ? { ...t, unread_count: 0 } : t))); window.dispatchEvent(new Event("lif:unread")); }).catch(() => toast.error("Conversation not found"));
  }, [threadId, user]);

  useDmSocket(!!user, (ev) => {
    if (ev.type !== "message") return;
    const m = ev.message;
    if (m.thread_id === threadId) {
      setMessages((xs) => (xs.some((x) => x.msg_id === m.msg_id) ? xs : [...xs, m]));
      if (m.sender_id !== user.user_id) api.post(`/dm/threads/${threadId}/read`).then(() => window.dispatchEvent(new Event("lif:unread"))).catch(() => {});
    }
    setThreads((ts) => {
      if (!ts?.some((t) => t.thread_id === m.thread_id)) { loadThreads(); return ts; }
      const next = ts.map((t) => (t.thread_id === m.thread_id ? { ...t, last_message: m.text, last_at: m.created_at, unread_count: m.thread_id === threadId || m.sender_id === user.user_id ? 0 : (t.unread_count || 0) + 1 } : t));
      return next.sort((a, b) => (b.last_at || "").localeCompare(a.last_at || ""));
    });
  });

  async function send(text) {
    try {
      const { data } = await api.post(`/dm/threads/${threadId}/messages`, { text });
      setMessages((xs) => (xs.some((x) => x.msg_id === data.msg_id) ? xs : [...xs, data]));
    } catch (e) { toast.error(formatApiError(e)); }
  }

  if (!user) return null;
  return (
    <div className="bg-[var(--bg)] h-[100dvh] flex flex-col" data-testid="messages-page">
      <div className={threadId ? "hidden md:block" : ""}><Navbar /></div>
      <div className="flex-1 min-h-0 container-wide md:px-12 md:py-6 w-full">
        <div className="h-full md:border md:border-[var(--line)] md:rounded-3xl overflow-hidden bg-white grid md:grid-cols-[320px_1fr]">
          <aside className={`${threadId ? "hidden md:flex" : "flex"} flex-col min-h-0 md:border-r border-[var(--line)]`}>
            <div className="px-4 py-4 border-b border-[var(--line)]"><p className="font-serif text-2xl">Messages</p></div>
            <div className="flex-1 overflow-y-auto pb-24 md:pb-0"><ThreadList threads={threads} active={threadId} /></div>
          </aside>
          <section className={`${threadId ? "flex" : "hidden md:flex"} flex-col min-h-0`}>
            {threadId && active ? <Chat thread={active} me={user.user_id} messages={messages} onSend={send} />
              : <div className="m-auto text-center text-[var(--muted)] p-8"><MessageCircle className="w-10 h-10 mx-auto text-[var(--gold)] mb-3" /><p className="font-serif text-2xl text-[var(--ink)]">Your conversations</p><p className="text-sm mt-1">Pick a conversation to start chatting.</p></div>}
          </section>
        </div>
      </div>
    </div>
  );
}
